import type { CompanyCandidate, CompanyNameSuggestion } from "@scoach/types";
import { Spinner } from "@scoach/ui";
import { useEffect, useRef, useState } from "react";

import { getBigQueryTokenSilent } from "../../lib/googleToken.ts";
import { useAuthStore } from "../auth/store.ts";
import { CompanyLogo } from "../discovery/CompanyLogo.tsx";
import { companiesApi } from "./api.ts";

/**
 * Kick off the company's billing/growth report the moment it's picked — so results
 * are ready before the user ever opens the Billing tab. Best-effort and silent: it
 * reuses a stored/GIS BigQuery token (never a popup), and the server caches the run
 * so the panel's own load is instant (and no double CRM call). No-op without consent.
 */
function prefetchBilling(company: string) {
  const c = company.trim();
  if (!c) return;
  void (async () => {
    try {
      const { bqAccessToken, bqTokenExp } = useAuthStore.getState();
      let token = bqAccessToken && (!bqTokenExp || bqTokenExp > Date.now()) ? bqAccessToken : "";
      if (!token) token = (await getBigQueryTokenSilent()) || "";
      if (!token) return;
      useAuthStore.getState().setBqToken(token, Date.now() + 55 * 60 * 1000);
      await companiesApi.report(c, token).catch(() => {});
    } catch {
      /* best-effort warm-up */
    }
  })();
}

/** What the caller receives when a company is chosen. */
export interface CompanyPick {
  name: string;
  domain?: string;
  logoUrl?: string;
  /** Present when the pick is an already-researched company (from prior meetings). */
  slug?: string;
  hasProfile?: boolean;
}

/**
 * Company picker with logos. As the user types we look up real companies via
 * Brandfetch (name + primary domain + logo) and show them — together with the
 * rep's prior companies — so they pick the RIGHT company (and its logo) BEFORE any
 * research/intel runs. Picking free-typed text is always allowed as a fallback.
 *
 * Used by Discovery, meeting setup, and the live company-intel panel.
 */
export function CompanyPicker({
  value,
  onChange,
  onSelect,
  priorSuggestions = [],
  placeholder = "Company name or website…",
  autoFocus,
  className = "setup-input",
}: {
  value: string;
  onChange: (v: string) => void;
  onSelect: (pick: CompanyPick) => void;
  /** The rep's already-known companies (cached profiles + prior accounts). */
  priorSuggestions?: CompanyNameSuggestion[];
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [matches, setMatches] = useState<CompanyCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);

  const q = value.trim();

  // Debounced Brandfetch lookup as the user types.
  useEffect(() => {
    if (q.length < 2) {
      setMatches([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const mine = ++seq.current;
    const t = setTimeout(() => {
      companiesApi
        .brandSearch(q)
        .then((r) => {
          if (mine !== seq.current) return;
          setMatches(r);
        })
        .catch(() => {
          if (mine === seq.current) setMatches([]);
        })
        .finally(() => {
          if (mine === seq.current) setLoading(false);
        });
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  // Prior companies that match what's typed (shown first, with a ✓).
  const priorMatches = q
    ? priorSuggestions.filter((s) => s.name.toLowerCase().includes(q.toLowerCase())).slice(0, 4)
    : [];
  // Don't repeat a prior company that Brandfetch also returned.
  const priorNames = new Set(priorMatches.map((s) => s.name.toLowerCase()));
  const brandMatches = matches.filter((m) => !priorNames.has(m.name.toLowerCase()));

  function choosePrior(s: CompanyNameSuggestion) {
    onChange(s.name);
    prefetchBilling(s.name);
    onSelect({ name: s.name, slug: s.slug, hasProfile: s.hasProfile });
    setOpen(false);
  }
  function chooseBrand(c: CompanyCandidate) {
    onChange(c.name);
    prefetchBilling(c.name);
    onSelect({ name: c.name, domain: c.domain, logoUrl: c.logoUrl });
    setOpen(false);
  }
  function chooseTyped() {
    const name = q;
    if (!name) return;
    prefetchBilling(name);
    // A URL/domain typed directly is already specific — pass it as the domain.
    const isUrl = /\.[a-z]{2,}$/i.test(name) && !name.includes(" ");
    onSelect(isUrl ? { name, domain: name } : { name });
    setOpen(false);
  }

  const showDropdown =
    open && (loading || priorMatches.length > 0 || brandMatches.length > 0 || q.length >= 2);

  return (
    <div style={{ position: "relative" }}>
      <input
        type="text"
        className={className}
        value={value}
        placeholder={placeholder}
        // biome-ignore lint/a11y/noAutofocus: the company field is the sole focus of its screen/modal
        autoFocus={autoFocus}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            // Prefer the top Brandfetch match if there is one, else free text.
            if (brandMatches[0]) chooseBrand(brandMatches[0]);
            else chooseTyped();
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
        onBlur={() => setTimeout(() => setOpen(false), 180)}
      />
      {showDropdown && (
        <div className="cpick-menu">
          {priorMatches.map((s) => (
            <button
              type="button"
              key={`p-${s.slug}`}
              className="cpick-item"
              onMouseDown={(e) => {
                e.preventDefault();
                choosePrior(s);
              }}
            >
              <CompanyLogo name={s.name} size={28} rounded={6} />
              <span className="cpick-name">{s.name}</span>
              {s.hasProfile && <span className="cpick-tag">Researched</span>}
            </button>
          ))}
          {brandMatches.map((c) => (
            <button
              type="button"
              key={`b-${c.domain ?? c.name}`}
              className="cpick-item"
              onMouseDown={(e) => {
                e.preventDefault();
                chooseBrand(c);
              }}
            >
              <CompanyLogo
                name={c.name}
                domain={c.domain}
                logoUrl={c.logoUrl}
                size={28}
                rounded={6}
              />
              <span className="cpick-name">{c.name}</span>
              {c.domain && <span className="cpick-dom">{c.domain}</span>}
            </button>
          ))}
          {loading && (
            <div className="cpick-item cpick-loading">
              <Spinner size={14} /> Searching companies…
            </div>
          )}
          {!loading && q.length >= 2 && (
            <button
              type="button"
              className="cpick-item cpick-use"
              onMouseDown={(e) => {
                e.preventDefault();
                chooseTyped();
              }}
            >
              + Use “{q}”
            </button>
          )}
        </div>
      )}
    </div>
  );
}
